const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const LEADING_AUDIO_FENCE_RE =
  /^```(?:yaml\s+audio|audio)\s*\r?\n[\s\S]*?\r?\n```(?:\r?\n)*/

export type ParsedAudioFence = {
  attachmentId: string | null
  audioDocumentId: string | null
  voiceId: string
  sourceHash: string | null
  src: string | null
}

export function stripLeadingAudioFence(body: string): string {
  return String(body || '').replace(LEADING_AUDIO_FENCE_RE, '')
}

export function parseAudioFenceYaml(body: string): ParsedAudioFence | null {
  const text = String(body || '')
  const attachmentMatch = text.match(/(?:^|\n)\s*attachment_id:\s*([^\s#]+)/i)
  const audioDocMatch = text.match(/(?:^|\n)\s*audio_document_id:\s*([^\s#]+)/i)
  const voiceMatch = text.match(/(?:^|\n)\s*voice_id:\s*([^\s#]+)/i)
  const hashMatch = text.match(/(?:^|\n)\s*source_hash:\s*([^\s#]+)/i)
  const srcMatch = text.match(/(?:^|\n)\s*src:\s*(\S+)/i)
  const attachmentId = attachmentMatch?.[1]?.trim() || null
  const src = srcMatch?.[1]?.trim() || null
  if (!attachmentId && !src) return null
  if (attachmentId && !UUID_RE.test(attachmentId)) return null
  return {
    attachmentId,
    audioDocumentId: audioDocMatch?.[1]?.trim() || null,
    voiceId: voiceMatch?.[1]?.trim() || 'eve',
    sourceHash: hashMatch?.[1]?.trim() || null,
    src,
  }
}

export function audioSrcForFence(fence: ParsedAudioFence): string | null {
  if (fence.src && /^https?:\/\//i.test(fence.src)) return fence.src
  if (fence.attachmentId) return `/api/attachments/${fence.attachmentId}/audio`
  return null
}

export async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export function looksLikeAudioFence(className: string | undefined, body: string): boolean {
  const cls = className || ''
  const parsed = parseAudioFenceYaml(body)
  if (!parsed) return false
  if (cls && !cls.includes('language-yaml') && !cls.includes('language-audio')) return false
  return /attachment_id\s*:/i.test(body) || /(?:^|\n)\s*src\s*:/i.test(body)
}

const LEADING_AUDIO_FENCE_BODY_RE =
  /^```(?:yaml\s+audio|audio)\s*\r?\n([\s\S]*?)\r?\n```/

/** Player source for a fence at the top of the lesson body. */
export function leadingAudioFence(body: string): ParsedAudioFence | null {
  const match = String(body || '').match(LEADING_AUDIO_FENCE_BODY_RE)
  if (!match) return null
  return parseAudioFenceYaml(match[1])
}
